import React from 'react';
import { Card, Descriptions, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getEmployeeById } from '@/api/employees';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';


interface EmployeeTabProps {
  employeeId: string;
}

const EmployeeTab: React.FC<EmployeeTabProps> = ({ employeeId }) => {
  const { t } = useTranslation(['employees', 'common']);
  const { firestoreUser } = useAuth();

  const { data: employee, isLoading, error } = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => getEmployeeById(employeeId, firestoreUser!),
    enabled: !!employeeId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !employee) {
    return (
      <Card>
        <Empty description={t('employees:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('employees:fields.employeeId')}>
          {employee.employeeId}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.user')}>
          {employee.user?.displayName || employee.id}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.role')}>
          {t(`employees:roles.${employee.role}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.status')}>
          {t(`employees:status.${employee.status}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.department')}>
          {employee.department}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.joiningDate')}>
          {employee.joiningDate ? dayjs(employee.joiningDate).format('DD/MM/YYYY') : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.exitDate')}>
          {employee.exitDate ? dayjs(employee.exitDate).format('DD/MM/YYYY') : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.supervisor')}>
          {employee.supervisor?.displayName || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.salary')}>
          {employee.salary ? `${employee.salary.amount} ${employee.salary.currency}` : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.contacts')}>
          {employee.contacts?.length ? `${employee.contacts.length} contact(s)` : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.shiftTiming')}>
          {employee.shiftTiming?.length ? `${employee.shiftTiming.length} schedule(s)` : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.activityRecords')}>
          {employee.documents?.length ? `${employee.documents.length} document(s)` : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('employees:fields.notes')}>
          {employee.notes || '-'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default EmployeeTab; 